<div class="min-h-screen bg-gray-900 py-6">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="mb-8">
      <h2 class="text-2xl font-bold leading-7 text-white sm:truncate sm:text-3xl">
        Settings
      </h2>
    </div>

    <!-- Domain Warning -->
    <div *ngIf="!currentDomain" 
         class="rounded-md bg-yellow-900/50 p-4 mb-6">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-yellow-400">Please select a domain first</h3>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="flex justify-center py-8">
      <svg class="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" 
         class="rounded-md bg-red-900/50 p-4 mb-6">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-400">{{ errorMessage }}</h3>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div *ngIf="currentDomain && !loading && !errorMessage"
         class="space-y-6 pb-24">
      
      <!-- Domain Info Section -->
      <div class="bg-gray-800/50 shadow overflow-hidden sm:rounded-lg">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-lg leading-6 font-medium text-white">
            Domain Settings
          </h3>
          <p class="mt-1 max-w-2xl text-sm text-gray-400">
            Current domain configuration
          </p>
        </div>
        <div class="border-t border-gray-700 px-4 py-5 sm:px-6">
          <p class="text-sm text-gray-300">
            <span class="font-medium text-gray-200">Current Domain:</span>
            {{ currentDomain }}
          </p>
        </div>
      </div>

      <!-- Website Settings Section -->
      <div class="bg-gray-800/50 shadow overflow-hidden sm:rounded-lg border border-gray-700/50">
        <div class="px-6 py-5">
          <h3 class="text-lg leading-6 font-medium text-white flex items-center gap-2">
            <svg class="h-5 w-5 text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fill-rule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clip-rule="evenodd" />
            </svg>
            Website Settings
          </h3>
          <p class="mt-1 text-sm text-gray-400">
            Configure website specific settings and preferences
          </p>
        </div>
        
        <div class="border-t border-gray-700">
          <form [formGroup]="settingsForm" class="divide-y divide-gray-700">
            <!-- Logo Section -->
            <div class="px-6 py-6 border-b border-gray-700">
              <div class="mb-6">
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <h4 class="text-sm font-medium text-gray-200">Website Logo</h4>
                    <p class="mt-1 text-sm text-gray-400">Upload your website logo (max 2MB)</p>
                  </div>
                </div>
                
                <!-- Logo Preview -->
                <div *ngIf="settingsForm.get('logo')?.value" class="mb-4">
                  <div class="flex items-start gap-4">
                    <img [src]="settingsForm.get('logo')?.value" 
                         alt="Website Logo" 
                         class="h-16 object-contain bg-gray-900 rounded-lg p-2">
                    
                    <button type="button"
                            (click)="onDeleteLogo()"
                            class="inline-flex items-center px-3 py-2 border border-red-500/30 
                                   text-sm font-medium rounded-md text-red-400 bg-red-900/20 
                                   hover:bg-red-900/40 focus:outline-none focus:ring-2 
                                   focus:ring-offset-2 focus:ring-red-500 focus:ring-offset-gray-900 
                                   transition-colors">
                      <svg class="mr-2 -ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" 
                           viewBox="0 0 24 24" fill="currentColor">
                        <path fill-rule="evenodd" 
                              d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z" 
                              clip-rule="evenodd" />
                      </svg>
                      Delete Logo
                    </button>
                  </div>
                </div>

                <!-- Upload Button -->
                <div class="flex items-center">
                  <label class="relative cursor-pointer">
                    <input type="file" 
                           class="hidden" 
                           accept="image/*"
                           (change)="onLogoUpload($event)"
                           [disabled]="logoUploading">
                    <span class="inline-flex items-center px-3 py-2 border border-gray-600 
                                 text-sm font-medium rounded-md text-gray-300 bg-gray-700 
                                 hover:bg-gray-600 focus:outline-none focus:ring-2 
                                 focus:ring-offset-2 focus:ring-indigo-500 focus:ring-offset-gray-900 
                                 transition-colors">
                      <svg class="mr-2 -ml-1 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" 
                           viewBox="0 0 24 24" fill="currentColor">
                        <path fill-rule="evenodd" 
                              d="M11.47 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06l-3.22-3.22V16.5a.75.75 0 01-1.5 0V4.81L8.03 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" 
                              clip-rule="evenodd" />
                      </svg>
                      {{ logoUploading ? 'Uploading...' : 'Upload Logo' }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Favicon Section -->
            <div class="px-6 py-6 border-b border-gray-700">
              <div class="mb-2">
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <h4 class="text-sm font-medium text-gray-200">Favicon</h4>
                    <p class="mt-1 text-sm text-gray-400">Upload a favicon (max 1MB). If empty, the logo is used.</p>
                  </div>
                </div>

                <!-- Favicon Preview -->
                <div *ngIf="settingsForm.get('favicon')?.value" class="mb-4">
                  <div class="flex items-start gap-4">
                    <img [src]="settingsForm.get('favicon')?.value"
                         alt="Favicon"
                         class="h-12 w-12 object-contain bg-gray-900 rounded-lg p-1.5">

                    <button type="button"
                            (click)="onDeleteFavicon()"
                            class="inline-flex items-center px-3 py-2 border border-red-500/30
                                   text-sm font-medium rounded-md text-red-400 bg-red-900/20
                                   hover:bg-red-900/40 focus:outline-none focus:ring-2
                                   focus:ring-offset-2 focus:ring-red-500 focus:ring-offset-gray-900
                                   transition-colors">
                      <svg class="mr-2 -ml-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg"
                           viewBox="0 0 24 24" fill="currentColor">
                        <path fill-rule="evenodd"
                              d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z"
                              clip-rule="evenodd" />
                      </svg>
                      Delete Favicon
                    </button>
                  </div>
                </div>

                <!-- Fallback info: show logo as current favicon -->
                <div *ngIf="!settingsForm.get('favicon')?.value && settingsForm.get('logo')?.value" class="mb-4">
                  <div class="flex items-center gap-3 text-xs text-gray-400">
                    <img [src]="settingsForm.get('logo')?.value"
                         alt="Logo as favicon"
                         class="h-8 w-8 object-contain bg-gray-900 rounded p-1 opacity-60">
                    <span>Currently using logo as favicon</span>
                  </div>
                </div>

                <!-- Upload Button -->
                <div class="flex items-center">
                  <label class="relative cursor-pointer">
                    <input type="file"
                           class="hidden"
                           accept="image/*"
                           (change)="onFaviconUpload($event)"
                           [disabled]="faviconUploading">
                    <span class="inline-flex items-center px-3 py-2 border border-gray-600
                                 text-sm font-medium rounded-md text-gray-300 bg-gray-700
                                 hover:bg-gray-600 focus:outline-none focus:ring-2
                                 focus:ring-offset-2 focus:ring-indigo-500 focus:ring-offset-gray-900
                                 transition-colors">
                      <svg class="mr-2 -ml-1 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg"
                           viewBox="0 0 24 24" fill="currentColor">
                        <path fill-rule="evenodd"
                              d="M11.47 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06l-3.22-3.22V16.5a.75.75 0 01-1.5 0V4.81L8.03 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z"
                              clip-rule="evenodd" />
                      </svg>
                      {{ faviconUploading ? 'Uploading...' : 'Upload Favicon' }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Languages Section -->
            <div class="px-6 py-6">
              <div class="mb-6">
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <h4 class="text-sm font-medium text-gray-200">Languages</h4>
                    <p class="mt-1 text-sm text-gray-400">Select the languages your website will support</p>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="px-2.5 py-1 text-xs font-medium rounded-full bg-indigo-900/50 text-indigo-300 border border-indigo-700/50">
                      {{ (settingsForm.get('languages')?.value || []).length }} Selected
                    </span>
                    <button type="button"
                            (click)="openAddLanguageModal()"
                            class="inline-flex items-center px-2.5 py-1.5 border border-gray-600 
                                   text-xs font-medium rounded-md text-gray-300 bg-gray-700 
                                   hover:bg-gray-600 focus:outline-none focus:ring-2 
                                   focus:ring-offset-2 focus:ring-indigo-500 focus:ring-offset-gray-900 
                                   transition-colors">
                      <svg class="mr-1.5 -ml-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                      </svg>
                      Add Language
                    </button>
                  </div>
                </div>
                <div class="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  <div *ngFor="let lang of availableLanguages" 
                       class="relative flex items-start py-1">
                    <div class="flex h-6 items-center">
                      <input type="checkbox" 
                             [value]="lang.code"
                             [id]="'language-' + lang.code"
                             (change)="onLanguageChange($event)"
                             [checked]="isLanguageSelected(lang.code)"
                             class="h-4 w-4 rounded border-gray-600 text-indigo-500 
                                    focus:ring-indigo-500 focus:ring-offset-gray-900
                                    bg-gray-700 cursor-pointer transition-colors">
                    </div>
                    <div class="ml-3 text-sm leading-6 flex-1">
                      <label [for]="'language-' + lang.code" 
                             class="text-gray-300 cursor-pointer select-none hover:text-gray-200 transition-colors">
                        {{lang.name}} 
                        <span class="text-gray-500">({{lang.code.toUpperCase()}})</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-6">
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <h4 class="text-sm font-medium text-gray-200">Default Language</h4>
                    <p class="mt-1 text-sm text-gray-400">Primary language for your website</p>
                  </div>
                </div>
                <select formControlName="defaultLanguage" 
                        class="mt-1 block w-full px-4 py-2.5 text-base border border-gray-600 
                               focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                               sm:text-sm rounded-md bg-gray-700/50 text-gray-200">
                  <option *ngFor="let lang of settingsForm.get('languages')?.value" 
                          [value]="lang">
                    {{ getLanguageName(lang) }}
                  </option>
                </select>

              </div>
            </div>


            <!-- SEO Section -->
            <div class="px-6 py-6" formGroupName="seo">
              <div class="flex items-center gap-2 mb-6">
                <svg class="h-5 w-5 text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75c-1.036 0-1.875-.84-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75C3.84 21.75 3 20.91 3 19.875v-6.75z" />
                </svg>
                <h4 class="text-sm font-medium text-gray-200">SEO Settings</h4>
              </div>
              
              <div class="space-y-6">
                <div>
                  <label class="block text-sm font-medium text-gray-300">Header Tags</label>
                  <p class="mt-1 text-sm text-gray-400">Scripts or tags to be placed in the &lt;head&gt; section</p>
                  <textarea formControlName="headerTags"
                          rows="3"
                          placeholder="<!-- Add your header tags here -->"
                          class="mt-2 block w-full px-4 py-2.5 border border-gray-600 rounded-md shadow-sm 
                                 bg-gray-700/50 text-gray-200 focus:ring-2 focus:ring-indigo-500 
                                 focus:border-indigo-500 sm:text-sm font-mono"></textarea>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300">Body Tags</label>
                  <p class="mt-1 text-sm text-gray-400">Scripts or tags to be placed at the start of the &lt;body&gt; section</p>
                  <textarea formControlName="bodyTags"
                          rows="3"
                          placeholder="<!-- Add your body tags here -->"
                          class="mt-2 block w-full px-4 py-2.5 border border-gray-600 rounded-md shadow-sm 
                                 bg-gray-700/50 text-gray-200 focus:ring-2 focus:ring-indigo-500 
                                 focus:border-indigo-500 sm:text-sm font-mono"></textarea>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300">Footer Scripts</label>
                  <p class="mt-1 text-sm text-gray-400">Scripts or tags to be placed at the end of the &lt;body&gt; section (before closing &lt;/body&gt; tag)</p>
                  <textarea formControlName="footerScripts"
                          rows="3"
                          placeholder="<!-- Add your footer scripts here -->"
                          class="mt-2 block w-full px-4 py-2.5 border border-gray-600 rounded-md shadow-sm 
                                 bg-gray-700/50 text-gray-200 focus:ring-2 focus:ring-indigo-500 
                                 focus:border-indigo-500 sm:text-sm font-mono"></textarea>
                </div>

              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- Theming Section -->
      <div class="bg-gray-800/50 shadow-xl overflow-hidden sm:rounded-xl border border-gray-700/50">
        <div class="px-6 py-5 bg-gradient-to-r from-gray-800/80 to-gray-800/50 border-b border-gray-700/50">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-xl leading-7 font-semibold text-white flex items-center gap-3">
                <div class="p-2 bg-indigo-500/10 rounded-lg">
                  <svg class="h-6 w-6 text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fill-rule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clip-rule="evenodd" />
                  </svg>
                </div>
                Theming
              </h3>
              <p class="mt-2 text-sm text-gray-400">
                Pas het uiterlijk en de layout van je website aan
              </p>
            </div>
          </div>
        </div>
        
        <div class="p-6 space-y-6">
          <!-- Theme Preset Selector -->
          <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6">
            <div class="flex items-center gap-3 mb-2">
              <div class="p-2 bg-amber-500/10 rounded-lg">
                <svg class="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <div>
                <h4 class="text-base font-semibold text-white">Thema Preset</h4>
                <p class="text-xs text-gray-400 mt-0.5">Kies een preset als startpunt, pas daarna de details hieronder aan</p>
              </div>
            </div>

            <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
              <button
                *ngFor="let tmpl of themeTemplates"
                type="button"
                (click)="onSelectTemplate(tmpl.id)"
                class="relative group rounded-xl border-2 p-4 text-left transition-all duration-200 hover:scale-[1.02]"
                [ngClass]="selectedTemplate === tmpl.id
                  ? 'border-indigo-500 bg-indigo-500/10 shadow-lg shadow-indigo-500/10'
                  : 'border-gray-700/50 bg-gray-800/20 hover:border-gray-600 hover:bg-gray-800/40'">
                <!-- Color preview -->
                <div class="flex items-center gap-1.5 mb-3">
                  <div class="w-8 h-8 rounded-lg border border-gray-600/30 shadow-inner"
                       [style.backgroundColor]="tmpl.previewColors.bg"></div>
                  <div class="w-8 h-8 rounded-lg border border-gray-600/30 shadow-inner"
                       [style.backgroundColor]="tmpl.previewColors.primary"></div>
                  <div class="w-8 h-8 rounded-lg border border-gray-600/30 shadow-inner"
                       [style.backgroundColor]="tmpl.previewColors.text"></div>
                </div>
                <!-- Name -->
                <p class="text-sm font-semibold text-gray-200 mb-1">{{ tmpl.name }}</p>
                <p class="text-xs text-gray-400 leading-relaxed line-clamp-2">{{ tmpl.description }}</p>
                <!-- Selected indicator -->
                <div *ngIf="selectedTemplate === tmpl.id"
                     class="absolute top-2 right-2">
                  <svg class="h-5 w-5 text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                  </svg>
                </div>
              </button>
            </div>
          </div>

          <!-- Navigation Theming Card -->
          <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="p-2 bg-blue-500/10 rounded-lg">
                <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </div>
              <div>
                <h4 class="text-base font-semibold text-white">Navigatie Menu</h4>
                <p class="text-xs text-gray-400 mt-0.5">Positie, kleuren en styling van het navigatiemenu</p>
              </div>
            </div>
            
            <form [formGroup]="themingForm">
              <div class="space-y-6">
                <!-- Layout Options -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Menu Position -->
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                      <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      Menu Positie
                    </label>
                    <select
                      formControlName="menuPosition"
                      class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                        hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                        transition-all duration-200">
                      <option value="left">Links</option>
                      <option value="center">Center</option>
                      <option value="right">Rechts</option>
                    </select>
                  </div>

                  <!-- Logo Position -->
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                      <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Logo Positie
                    </label>
                    <select
                      formControlName="logoPosition"
                      class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                        hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                        transition-all duration-200">
                      <option value="left">Links</option>
                      <option value="center">Center</option>
                      <option value="right">Rechts</option>
                      <option value="separate">Apart (los van menu)</option>
                    </select>
                  </div>
                </div>

                <!-- Background Color -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    Achtergrond Kleur
                  </label>
                  <div class="flex items-center gap-3">
                    <div class="relative">
                      <input
                        type="color"
                        formControlName="backgroundColor"
                        class="h-12 w-16 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 
                          transition-colors shadow-lg">
                    </div>
                    <input
                      type="text"
                      formControlName="backgroundColor"
                      class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                        hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                        transition-all duration-200 font-mono">
                  </div>
                </div>

                <!-- Text Colors -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Menu Kleuren
                  </label>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Tekst Kleur</label>
                      <div class="flex items-center gap-2">
                        <input
                          type="color"
                          formControlName="textColor"
                          class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500
                            transition-colors shadow">
                        <input
                          type="text"
                          formControlName="textColor"
                          class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                            transition-all duration-200 font-mono">
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Hover Kleur</label>
                      <div class="flex items-center gap-2">
                        <input
                          type="color"
                          formControlName="textHoverColor"
                          class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500
                            transition-colors shadow">
                        <input
                          type="text"
                          formControlName="textHoverColor"
                          class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                            transition-all duration-200 font-mono">
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Actief Kleur</label>
                      <div class="flex items-center gap-2">
                        <input
                          type="color"
                          formControlName="activeColor"
                          class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500
                            transition-colors shadow">
                        <input
                          type="text"
                          formControlName="activeColor"
                          class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                            transition-all duration-200 font-mono">
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Border Settings -->
                <div class="bg-gray-800/20 rounded-lg p-4 border border-gray-700/30">
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                      <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                      </svg>
                      <label for="borderEnabled" class="text-sm font-medium text-gray-300 cursor-pointer">Border Inschakelen</label>
                    </div>
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        formControlName="borderEnabled"
                        id="borderEnabled"
                        class="sr-only peer">
                      <div class="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-500/20 
                        rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] 
                        after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border 
                        after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                  
                  <div *ngIf="themingForm.get('borderEnabled')?.value" class="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-700/30">
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Border Kleur</label>
                      <div class="flex items-center gap-2">
                        <input
                          type="color"
                          formControlName="borderColor"
                          class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 
                            transition-colors shadow">
                        <input
                          type="text"
                          formControlName="borderColor"
                          class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200 
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                            transition-all duration-200 font-mono">
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Border Dikte (px)</label>
                      <input
                        type="number"
                        formControlName="borderWidth"
                        min="0"
                        max="10"
                        class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                          hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                          transition-all duration-200">
                    </div>
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Border Stijl</label>
                      <select
                        formControlName="borderStyle"
                        class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                          hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                          transition-all duration-200">
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Spacing -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <!-- Margins -->
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                      <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Margins (px)
                    </label>
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label class="block text-xs text-gray-400 mb-1.5">Links</label>
                        <input
                          type="number"
                          formControlName="marginLeft"
                          min="0"
                          class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                            transition-all duration-200">
                      </div>
                      <div>
                        <label class="block text-xs text-gray-400 mb-1.5">Rechts</label>
                        <input
                          type="number"
                          formControlName="marginRight"
                          min="0"
                          class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                            transition-all duration-200">
                      </div>
                    </div>
                  </div>

                  <!-- Padding & Height -->
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                      <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      Afmetingen (px)
                    </label>
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label class="block text-xs text-gray-400 mb-1.5">Padding</label>
                        <input
                          type="number"
                          formControlName="padding"
                          min="0"
                          class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                            transition-all duration-200">
                      </div>
                      <div>
                        <label class="block text-xs text-gray-400 mb-1.5">Hoogte</label>
                        <input
                          type="number"
                          formControlName="height"
                          min="40"
                          max="120"
                          class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                            transition-all duration-200">
                      </div>
                    </div>
                  </div>
                </div>

                <!-- CTA Button -->
                <div class="bg-gray-800/20 rounded-lg p-4 border border-gray-700/30">
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                      <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                      </svg>
                      <label for="ctaEnabled" class="text-sm font-medium text-gray-300 cursor-pointer">Contact / CTA Button</label>
                    </div>
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        formControlName="ctaEnabled"
                        id="ctaEnabled"
                        class="sr-only peer">
                      <div class="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-500/20
                        rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                        after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border
                        after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  <div *ngIf="themingForm.get('ctaEnabled')?.value" class="space-y-4 pt-4 border-t border-gray-700/30">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-xs text-gray-400 mb-2">Button Tekst</label>
                        <input
                          type="text"
                          formControlName="ctaText"
                          placeholder="Contact"
                          class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                            transition-all duration-200 placeholder-gray-500">
                      </div>
                      <div>
                        <label class="block text-xs text-gray-400 mb-2">URL</label>
                        <input
                          type="text"
                          formControlName="ctaUrl"
                          placeholder="/contact of https://..."
                          class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                            transition-all duration-200 placeholder-gray-500">
                      </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-xs text-gray-400 mb-2">Achtergrond Kleur</label>
                        <div class="flex items-center gap-2">
                          <input
                            type="color"
                            formControlName="ctaBgColor"
                            class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500
                              transition-colors shadow">
                          <input
                            type="text"
                            formControlName="ctaBgColor"
                            placeholder="Thema kleur"
                            class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                              hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                              transition-all duration-200 font-mono placeholder-gray-500">
                        </div>
                      </div>
                      <div>
                        <label class="block text-xs text-gray-400 mb-2">Tekst Kleur</label>
                        <div class="flex items-center gap-2">
                          <input
                            type="color"
                            formControlName="ctaTextColor"
                            class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500
                              transition-colors shadow">
                          <input
                            type="text"
                            formControlName="ctaTextColor"
                            class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                              hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                              transition-all duration-200 font-mono">
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center">
                      <input
                        type="checkbox"
                        formControlName="ctaOpenInNewTab"
                        id="ctaOpenInNewTab"
                        class="w-4 h-4 rounded bg-gray-700 border-gray-600 text-indigo-600 focus:ring-indigo-500">
                      <label for="ctaOpenInNewTab" class="ml-2 text-sm text-gray-300">Open in nieuw tabblad</label>
                    </div>
                  </div>
                </div>

              </div>
            </form>
          </div>

          <!-- Global Theming Card -->
          <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="p-2 bg-purple-500/10 rounded-lg">
                <svg class="h-5 w-5 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <div>
                <h4 class="text-base font-semibold text-white">Globale Theming</h4>
                <p class="text-xs text-gray-400 mt-0.5">Fonts, kleuren en typografie voor de hele website</p>
              </div>
            </div>
            
            <form [formGroup]="globalThemingForm">
              <div class="space-y-6">
                <!-- Font Family -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Font Familie
                  </label>
                  <select
                    formControlName="fontFamily"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                      hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                      transition-all duration-200">
                    <option *ngFor="let font of availableFonts" [value]="font.value">{{ font.label }}</option>
                  </select>
                </div>

                <!-- Background Color -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    Achtergrond Kleur
                  </label>
                  <div class="flex items-center gap-3">
                    <div class="relative">
                      <input
                        type="color"
                        formControlName="backgroundColor"
                        class="h-12 w-16 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 
                          transition-colors shadow-lg">
                    </div>
                    <input
                      type="text"
                      formControlName="backgroundColor"
                      class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                        hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                        transition-all duration-200 font-mono">
                  </div>
                </div>

                <!-- Text Color -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    Tekst Kleur
                  </label>
                  <div class="flex items-center gap-3">
                    <div class="relative">
                      <input
                        type="color"
                        formControlName="textColor"
                        class="h-12 w-16 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 
                          transition-colors shadow-lg">
                    </div>
                    <input
                      type="text"
                      formControlName="textColor"
                      class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                        hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                        transition-all duration-200 font-mono">
                  </div>
                </div>

                <!-- Link Colors -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    Link Kleuren
                  </label>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Normaal</label>
                      <div class="flex items-center gap-2">
                        <input
                          type="color"
                          formControlName="linkColor"
                          class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 
                            transition-colors shadow">
                        <input
                          type="text"
                          formControlName="linkColor"
                          class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200 
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                            transition-all duration-200 font-mono">
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs text-gray-400 mb-2">Hover</label>
                      <div class="flex items-center gap-2">
                        <input
                          type="color"
                          formControlName="linkHoverColor"
                          class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 
                            transition-colors shadow">
                        <input
                          type="text"
                          formControlName="linkHoverColor"
                          class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200 
                            hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                            transition-all duration-200 font-mono">
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Body Font Size -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Body Tekst Grootte (px)
                  </label>
                  <input
                    type="number"
                    formControlName="bodyFontSize"
                    min="10"
                    max="24"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                      hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                      transition-all duration-200">
                </div>

                <!-- Heading Options per Level -->
                <div class="bg-gray-800/20 rounded-lg p-5 border border-gray-700/30">
                  <h5 class="text-sm font-semibold text-gray-200 mb-5 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Heading Stijlen per Niveau
                  </h5>
                  
                  <div class="space-y-4">
                    <ng-container *ngFor="let level of headingLevels">
                      <div class="bg-gray-800/30 rounded-lg border border-gray-700/20 overflow-hidden transition-all duration-200">
                        <!-- Header (Clickable) -->
                        <button
                          type="button"
                          (click)="toggleHeading(level)"
                          class="w-full flex items-center justify-between p-4 hover:bg-gray-800/40 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20">
                          <div class="flex items-center gap-3">
                            <svg 
                              class="h-5 w-5 text-indigo-400 transition-transform duration-200"
                              [class.rotate-90]="isHeadingExpanded(level)"
                              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                            <h6 class="text-sm font-bold text-indigo-400 uppercase tracking-wider">{{ level.toUpperCase() }}</h6>
                          </div>
                          <svg 
                            class="h-5 w-5 text-gray-400 transition-transform duration-200"
                            [class.rotate-180]="isHeadingExpanded(level)"
                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        <!-- Content (Collapsible) -->
                        <div 
                          *ngIf="isHeadingExpanded(level)"
                          class="px-4 pb-4 border-t border-gray-700/20 pt-4 animate-in slide-in-from-top-2 duration-200">
                          <div [formGroup]="getHeadingFormGroup(level)" class="space-y-4">
                          <!-- Color & Font Size -->
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Kleur</label>
                              <div class="flex items-center gap-2">
                                <input
                                  type="color"
                                  formControlName="color"
                                  class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 
                                    transition-colors shadow">
                                <input
                                  type="text"
                                  formControlName="color"
                                  class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200 
                                    hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                    transition-all duration-200 font-mono">
                              </div>
                            </div>
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Font Grootte (px)</label>
                              <input
                                type="number"
                                formControlName="fontSize"
                                [min]="level === 'h1' ? 24 : level === 'h2' ? 20 : level === 'h3' ? 18 : 14"
                                [max]="level === 'h1' ? 72 : level === 'h2' ? 60 : level === 'h3' ? 48 : 36"
                                class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                                  hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                  transition-all duration-200">
                            </div>
                          </div>

                          <!-- Font Weight & Style -->
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Font Gewicht</label>
                              <select
                                formControlName="fontWeight"
                                class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                                  hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                  transition-all duration-200">
                                <option value="100">Thin (100)</option>
                                <option value="200">Extra Light (200)</option>
                                <option value="300">Light (300)</option>
                                <option value="400">Normal (400)</option>
                                <option value="500">Medium (500)</option>
                                <option value="600">Semi Bold (600)</option>
                                <option value="700">Bold (700)</option>
                                <option value="800">Extra Bold (800)</option>
                                <option value="900">Black (900)</option>
                              </select>
                            </div>
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Font Stijl</label>
                              <select
                                formControlName="fontStyle"
                                class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                                  hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                  transition-all duration-200">
                                <option value="normal">Normaal</option>
                                <option value="italic">Cursief</option>
                              </select>
                            </div>
                          </div>

                          <!-- Text Transform & Decoration -->
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Text Transform</label>
                              <select
                                formControlName="textTransform"
                                class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                                  hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                  transition-all duration-200">
                                <option value="none">Geen</option>
                                <option value="uppercase">HOOFDLETTERS</option>
                                <option value="lowercase">kleine letters</option>
                                <option value="capitalize">Eerste Letter Hoofdletter</option>
                              </select>
                            </div>
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Text Decoratie</label>
                              <select
                                formControlName="textDecoration"
                                class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                                  hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                  transition-all duration-200">
                                <option value="none">Geen</option>
                                <option value="underline">Onderstrepen</option>
                                <option value="line-through">Doorhalen</option>
                              </select>
                            </div>
                          </div>

                          <!-- Letter Spacing & Line Height -->
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Letter Spacing (px)</label>
                              <input
                                type="number"
                                formControlName="letterSpacing"
                                min="-2"
                                max="5"
                                step="0.1"
                                class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                                  hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                  transition-all duration-200">
                            </div>
                            <div>
                              <label class="block text-xs text-gray-400 mb-2">Regelhoogte</label>
                              <input
                                type="number"
                                formControlName="lineHeight"
                                min="0.8"
                                max="2.5"
                                step="0.1"
                                class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2.5 text-sm text-gray-200 
                                  hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                                  transition-all duration-200">
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    </ng-container>
                  </div>
                </div>

                <!-- Line Height -->
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2.5 flex items-center gap-2">
                    <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Regelhoogte Body Tekst
                  </label>
                  <input
                    type="number"
                    formControlName="lineHeight"
                    min="1"
                    max="3"
                    step="0.1"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200 
                      hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 
                      transition-all duration-200">
                  <p class="mt-1.5 text-xs text-gray-500">Aanbevolen: 1.5 - 1.8</p>
                </div>
              </div>
            </form>
          </div>

        </div>
      </div>

      <!-- Website Melding Section -->
      <div class="bg-gray-800/50 shadow-xl overflow-hidden sm:rounded-xl border border-gray-700/50">
        <div class="px-4 py-5 sm:px-6 border-b border-gray-700/50">
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center">
              <svg class="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-semibold text-white">Website Melding</h3>
              <p class="text-xs text-gray-400">Notificatiebalk bovenaan de website ({{ currentDomain }})</p>
            </div>
          </div>
        </div>
        <div class="px-4 py-5 sm:p-6 space-y-4">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-gray-300">Melding actief</label>
            <button (click)="websiteBannerEnabled = !websiteBannerEnabled" type="button"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
              [ngClass]="websiteBannerEnabled ? 'bg-indigo-600' : 'bg-gray-700'">
              <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200"
                [ngClass]="websiteBannerEnabled ? 'translate-x-5' : 'translate-x-0'"></span>
            </button>
          </div>
          <div *ngIf="websiteBannerEnabled" class="space-y-4">
            <div class="rounded-lg overflow-hidden">
              <div class="text-center py-2 px-4" [style.background-color]="websiteBannerBgColor" [style.color]="websiteBannerTextColor">
                <p class="text-sm m-0">{{ websiteBannerTextNl || 'Voorbeeld meldingstekst...' }}</p>
              </div>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">NL</label>
              <input type="text" [(ngModel)]="websiteBannerTextNl" placeholder="Meldingstekst" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">EN</label>
              <input type="text" [(ngModel)]="websiteBannerTextEn" placeholder="Notification text" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">DE</label>
              <input type="text" [(ngModel)]="websiteBannerTextDe" placeholder="Benachrichtigungstext" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">FR</label>
              <input type="text" [(ngModel)]="websiteBannerTextFr" placeholder="Texte de notification" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">ES</label>
              <input type="text" [(ngModel)]="websiteBannerTextEs" placeholder="Texto de notificación" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-gray-400 mb-1">Achtergrond</label>
                <div class="flex items-center gap-2">
                  <input type="color" [(ngModel)]="websiteBannerBgColor" class="w-8 h-8 rounded cursor-pointer bg-transparent border-0">
                  <input type="text" [(ngModel)]="websiteBannerBgColor" class="flex-1 rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2 font-mono">
                </div>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-400 mb-1">Tekst</label>
                <div class="flex items-center gap-2">
                  <input type="color" [(ngModel)]="websiteBannerTextColor" class="w-8 h-8 rounded cursor-pointer bg-transparent border-0">
                  <input type="text" [(ngModel)]="websiteBannerTextColor" class="flex-1 rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2 font-mono">
                </div>
              </div>
            </div>
          </div>
          <button (click)="saveWebsiteBanner()" type="button"
            class="w-full inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 transition-colors">
            Opslaan
          </button>
        </div>
      </div>

      <!-- Shop Melding Section -->
      <div class="bg-gray-800/50 shadow-xl overflow-hidden sm:rounded-xl border border-gray-700/50">
        <div class="px-4 py-5 sm:px-6 border-b border-gray-700/50">
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
              <svg class="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-semibold text-white">Shop Melding</h3>
              <p class="text-xs text-gray-400">Notificatiebalk bovenaan de webshop</p>
            </div>
          </div>
        </div>
        <div class="px-4 py-5 sm:p-6 space-y-4">
          <div class="flex items-center justify-between">
            <label class="text-sm font-medium text-gray-300">Melding actief</label>
            <button (click)="shopBannerEnabled = !shopBannerEnabled" type="button"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
              [ngClass]="shopBannerEnabled ? 'bg-indigo-600' : 'bg-gray-700'">
              <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200"
                [ngClass]="shopBannerEnabled ? 'translate-x-5' : 'translate-x-0'"></span>
            </button>
          </div>
          <div *ngIf="shopBannerEnabled" class="space-y-4">
            <div class="rounded-lg overflow-hidden">
              <div class="text-center py-2 px-4" [style.background-color]="shopBannerBgColor" [style.color]="shopBannerTextColor">
                <p class="text-sm m-0">{{ shopBannerTextNl || 'Voorbeeld meldingstekst...' }}</p>
              </div>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">NL</label>
              <input type="text" [(ngModel)]="shopBannerTextNl" placeholder="Gratis verzending vanaf €85" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">EN</label>
              <input type="text" [(ngModel)]="shopBannerTextEn" placeholder="Free shipping from €85" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">DE</label>
              <input type="text" [(ngModel)]="shopBannerTextDe" placeholder="Kostenloser Versand ab €85" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">FR</label>
              <input type="text" [(ngModel)]="shopBannerTextFr" placeholder="Livraison gratuite à partir de €85" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-400 mb-1">ES</label>
              <input type="text" [(ngModel)]="shopBannerTextEs" placeholder="Envío gratis a partir de €85" class="w-full rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2">
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium text-gray-400 mb-1">Achtergrond</label>
                <div class="flex items-center gap-2">
                  <input type="color" [(ngModel)]="shopBannerBgColor" class="w-8 h-8 rounded cursor-pointer bg-transparent border-0">
                  <input type="text" [(ngModel)]="shopBannerBgColor" class="flex-1 rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2 font-mono">
                </div>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-400 mb-1">Tekst</label>
                <div class="flex items-center gap-2">
                  <input type="color" [(ngModel)]="shopBannerTextColor" class="w-8 h-8 rounded cursor-pointer bg-transparent border-0">
                  <input type="text" [(ngModel)]="shopBannerTextColor" class="flex-1 rounded-lg bg-gray-700 border-gray-600 text-white text-sm px-3 py-2 font-mono">
                </div>
              </div>
            </div>
          </div>
          <button (click)="saveShopBanner()" type="button"
            class="w-full inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 transition-colors">
            Opslaan
          </button>
        </div>
      </div>

      <!-- Contact & Footer Section -->
      <div class="bg-gray-800/50 shadow-xl overflow-hidden sm:rounded-xl border border-gray-700/50">
        <div class="px-6 py-5 bg-gradient-to-r from-gray-800/80 to-gray-800/50 border-b border-gray-700/50">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-xl leading-7 font-semibold text-white flex items-center gap-3">
                <div class="p-2 bg-orange-500/10 rounded-lg">
                  <svg class="h-6 w-6 text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                Contactgegevens & Footer
              </h3>
              <p class="mt-2 text-sm text-gray-400">
                Bedrijfsgegevens, contactinfo en social media links. Wordt getoond in de footer en topbar.
              </p>
            </div>
          </div>
        </div>

        <div class="p-6 space-y-6">
          <form [formGroup]="footerForm">
            <!-- Footer Logo -->
            <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5 mb-6">
              <h5 class="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Footer Logo
              </h5>
              <p class="text-xs text-gray-400 mb-4">Optioneel apart logo voor de footer. Als er geen is ingesteld, wordt het website logo gebruikt.</p>

              <!-- Preview -->
              <div *ngIf="footerForm.get('footerLogo')?.value" class="mb-4">
                <div class="flex items-start gap-4">
                  <img [src]="footerForm.get('footerLogo')?.value"
                       alt="Footer Logo"
                       class="h-14 object-contain bg-gray-900 rounded-lg p-2">
                  <button type="button"
                          (click)="onDeleteFooterLogo()"
                          class="inline-flex items-center px-3 py-2 border border-red-500/30
                                 text-sm font-medium rounded-md text-red-400 bg-red-900/20
                                 hover:bg-red-900/40 transition-colors">
                    <svg class="mr-2 -ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path fill-rule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z" clip-rule="evenodd" />
                    </svg>
                    Verwijderen
                  </button>
                </div>
              </div>

              <!-- Fallback info -->
              <div *ngIf="!footerForm.get('footerLogo')?.value && settingsForm.get('logo')?.value" class="mb-4">
                <div class="flex items-center gap-3 text-xs text-gray-400">
                  <img [src]="settingsForm.get('logo')?.value"
                       alt="Website logo als footer logo"
                       class="h-8 object-contain bg-gray-900 rounded p-1 opacity-60">
                  <span>Gebruikt momenteel het website logo</span>
                </div>
              </div>

              <!-- Upload -->
              <label class="relative cursor-pointer inline-block">
                <input type="file"
                       class="hidden"
                       accept="image/*"
                       (change)="onFooterLogoUpload($event)"
                       [disabled]="footerLogoUploading">
                <span class="inline-flex items-center px-3 py-2 border border-gray-600
                             text-sm font-medium rounded-md text-gray-300 bg-gray-700
                             hover:bg-gray-600 transition-colors">
                  <svg class="mr-2 -ml-1 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fill-rule="evenodd" d="M11.47 2.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 01-1.06 1.06l-3.22-3.22V16.5a.75.75 0 01-1.5 0V4.81L8.03 8.03a.75.75 0 01-1.06-1.06l4.5-4.5zM3 15.75a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
                  </svg>
                  {{ footerLogoUploading ? 'Uploaden...' : 'Footer Logo Uploaden' }}
                </span>
              </label>
            </div>

            <!-- Company Info -->
            <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5 mb-6">
              <h5 class="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Bedrijfsgegevens
              </h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-gray-400 mb-2">Bedrijfsnaam</label>
                  <input type="text" formControlName="companyName" placeholder="Uw bedrijfsnaam"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
                <div>
                  <label class="block text-xs text-gray-400 mb-2">Adres</label>
                  <input type="text" formControlName="address" placeholder="Straat 1, 1234 AB Stad"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
              </div>
              <div class="mt-4">
                <label class="block text-xs text-gray-400 mb-2">Omschrijving</label>
                <textarea formControlName="description" rows="2" placeholder="Korte bedrijfsomschrijving"
                  class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                    placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                    focus:ring-indigo-500/20 transition-all duration-200"></textarea>
              </div>
            </div>

            <!-- Contact Info -->
            <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5 mb-6">
              <h5 class="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contactgegevens
              </h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-gray-400 mb-2">Telefoonnummer</label>
                  <input type="text" formControlName="phone" placeholder="+31 6 12345678"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
                <div>
                  <label class="block text-xs text-gray-400 mb-2">E-mailadres</label>
                  <input type="email" formControlName="email" placeholder="info@voorbeeld.nl"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
              </div>
            </div>

            <!-- CTA -->
            <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5 mb-6">
              <h5 class="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Call to Action (Footer)
              </h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-gray-400 mb-2">Button Tekst</label>
                  <input type="text" formControlName="ctaText" placeholder="Neem contact op"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
                <div>
                  <label class="block text-xs text-gray-400 mb-2">Button URL</label>
                  <input type="text" formControlName="ctaUrl" placeholder="/contact"
                    class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
              </div>
            </div>

            <!-- Social Media -->
            <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5 mb-6">
              <div class="flex items-center justify-between mb-4">
                <h5 class="text-sm font-semibold text-gray-200 flex items-center gap-2">
                  <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                  Social Media
                </h5>
                <div class="flex items-center gap-3">
                  <span class="text-xs text-gray-400">Toon in footer</span>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" formControlName="showSocials" class="sr-only peer">
                    <div class="w-9 h-5 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-500/20
                      rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                      after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border
                      after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
              </div>
              <div formGroupName="socials">
              <div class="space-y-3">
                <div class="flex items-center gap-3">
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  <input type="url" formControlName="linkedin" placeholder="https://linkedin.com/company/..."
                    class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
                <div class="flex items-center gap-3">
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  <input type="url" formControlName="facebook" placeholder="https://facebook.com/..."
                    class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
                <div class="flex items-center gap-3">
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  <input type="url" formControlName="instagram" placeholder="https://instagram.com/..."
                    class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
                <div class="flex items-center gap-3">
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  <input type="url" formControlName="twitter" placeholder="https://x.com/..."
                    class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
                <div class="flex items-center gap-3">
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  <input type="url" formControlName="youtube" placeholder="https://youtube.com/..."
                    class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2 text-sm text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                      focus:ring-indigo-500/20 transition-all duration-200">
                </div>
              </div>
              </div>
            </div>
          </form>

          <!-- Footer Columns -->
          <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5 mb-6">
            <div class="flex items-center justify-between mb-4">
              <h5 class="text-sm font-semibold text-gray-200 flex items-center gap-2">
                <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Footer Kolommen ({{ footerColumns.length }}/3)
              </h5>
              <button *ngIf="footerColumns.length < 3" (click)="addFooterColumn()" type="button"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-600/20 text-orange-400 border border-orange-600/30 hover:bg-orange-600/30 transition-colors">
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                Kolom toevoegen
              </button>
            </div>
            <p class="text-xs text-gray-400 mb-4">Voeg tot 3 kolommen toe met links die in de footer worden getoond. Laat leeg om automatisch het menu te gebruiken.</p>

            <div *ngIf="footerColumns.length === 0" class="text-center py-6 text-gray-500 text-sm">
              <p>Geen kolommen ingesteld. De footer toont automatisch het navigatiemenu.</p>
            </div>

            <div class="space-y-4">
              <div *ngFor="let col of footerColumns; let ci = index"
                class="bg-gray-900/40 rounded-lg border border-gray-700/40 p-4">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-2 flex-1">
                    <span class="text-xs text-gray-500 font-medium">Kolom {{ ci + 1 }}</span>
                    <input type="text" [(ngModel)]="col.title" placeholder="Kolomtitel"
                      class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-1.5 text-sm text-gray-200
                        placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                        focus:ring-indigo-500/20 transition-all duration-200">
                  </div>
                  <button (click)="removeFooterColumn(ci)" type="button"
                    class="ml-2 p-1.5 text-red-400/70 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                  </button>
                </div>

                <!-- Links -->
                <div class="space-y-2 ml-4">
                  <div *ngFor="let link of col.links; let li = index" class="flex items-center gap-2">
                    <input type="text" [(ngModel)]="link.label" placeholder="Label"
                      class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-1.5 text-xs text-gray-200
                        placeholder-gray-500 hover:bg-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200">
                    <input type="text" [(ngModel)]="link.url" placeholder="/pagina of https://..."
                      class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-1.5 text-xs text-gray-200
                        placeholder-gray-500 hover:bg-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 font-mono">
                    <button (click)="removeColumnLink(ci, li)" type="button"
                      class="p-1 text-gray-500 hover:text-red-400 transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                  <button (click)="addColumnLink(ci)" type="button"
                    class="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors mt-1">
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                    Link toevoegen
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Bar -->
          <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5 mb-6">
            <h5 class="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
              <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 13H5v-2h14v2z" />
              </svg>
              Footer Onderbalk
            </h5>
            <form [formGroup]="footerForm">
              <div class="mb-4">
                <label class="block text-xs text-gray-400 mb-2">Copyright / ondertekst (leeg = automatisch)</label>
                <input type="text" formControlName="bottomText" placeholder="© 2026 Bedrijfsnaam. Alle rechten voorbehouden."
                  class="w-full rounded-lg bg-gray-700/50 border border-gray-600/50 px-4 py-2.5 text-sm text-gray-200
                    placeholder-gray-500 hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2
                    focus:ring-indigo-500/20 transition-all duration-200">
              </div>
            </form>
            <div class="mt-4">
              <div class="flex items-center justify-between mb-3">
                <label class="text-xs text-gray-400">Onderste links (bijv. Privacy, Voorwaarden)</label>
                <button (click)="addBottomLink()" type="button"
                  class="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
                  Link toevoegen
                </button>
              </div>
              <div class="space-y-2">
                <div *ngFor="let link of footerBottomLinks; let i = index" class="flex items-center gap-2">
                  <input type="text" [(ngModel)]="link.label" placeholder="Label"
                    class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-1.5 text-xs text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200">
                  <input type="text" [(ngModel)]="link.url" placeholder="/privacy"
                    class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-1.5 text-xs text-gray-200
                      placeholder-gray-500 hover:bg-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 font-mono">
                  <button (click)="removeBottomLink(i)" type="button"
                    class="p-1 text-gray-500 hover:text-red-400 transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Top Bar Section -->
      <div class="bg-gray-800/50 shadow-xl overflow-hidden sm:rounded-xl border border-gray-700/50">
        <div class="px-6 py-5 bg-gradient-to-r from-gray-800/80 to-gray-800/50 border-b border-gray-700/50">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-xl leading-7 font-semibold text-white flex items-center gap-3">
                <div class="p-2 bg-teal-500/10 rounded-lg">
                  <svg class="h-6 w-6 text-teal-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 6v14a2 2 0 002 2h12a2 2 0 002-2V6M4 6l1-3h14l1 3" />
                  </svg>
                </div>
                Top Bar
              </h3>
              <p class="mt-2 text-sm text-gray-400">
                Balk boven de navigatie met adres, contactgegevens en social media links
              </p>
            </div>
          </div>
        </div>

        <div class="p-6 space-y-6">
          <form [formGroup]="topbarForm">
            <!-- Enable Toggle -->
            <div class="flex items-center justify-between mb-6 bg-gray-800/30 rounded-xl border border-gray-700/30 p-4">
              <div class="flex items-center gap-3">
                <div class="p-2 bg-teal-500/10 rounded-lg">
                  <svg class="h-5 w-5 text-teal-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-white">Top Bar Inschakelen</h4>
                  <p class="text-xs text-gray-400 mt-0.5">Toon een smalle balk boven het navigatiemenu</p>
                </div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" formControlName="enabled" class="sr-only peer">
                <div class="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-500/20
                  rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                  after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border
                  after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>

            <div *ngIf="topbarForm.get('enabled')?.value" class="space-y-6">
              <!-- Colors -->
              <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5">
                <h5 class="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                  <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Kleuren
                </h5>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs text-gray-400 mb-2">Achtergrond</label>
                    <div class="flex items-center gap-2">
                      <input type="color" formControlName="backgroundColor"
                        class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 transition-colors shadow">
                      <input type="text" formControlName="backgroundColor"
                        class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                          hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                          transition-all duration-200 font-mono">
                    </div>
                  </div>
                  <div>
                    <label class="block text-xs text-gray-400 mb-2">Tekst</label>
                    <div class="flex items-center gap-2">
                      <input type="color" formControlName="textColor"
                        class="h-10 w-12 rounded-lg border-2 border-gray-600/50 cursor-pointer hover:border-gray-500 transition-colors shadow">
                      <input type="text" formControlName="textColor"
                        class="flex-1 rounded-lg bg-gray-700/50 border border-gray-600/50 px-3 py-2 text-sm text-gray-200
                          hover:bg-gray-700 hover:border-gray-600 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20
                          transition-all duration-200 font-mono">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Toggle items -->
              <div class="bg-gray-800/30 rounded-xl border border-gray-700/30 p-5">
                <h5 class="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
                  <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Zichtbare Elementen
                </h5>
                <p class="text-xs text-gray-400 mb-4">Kies welke gegevens worden getoond in de top bar. De gegevens worden overgenomen uit de footer instellingen.</p>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div class="flex items-center gap-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" formControlName="showAddress" class="sr-only peer">
                      <div class="w-9 h-5 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-500/20
                        rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                        after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border
                        after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                    <span class="text-sm text-gray-300">Adres</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" formControlName="showPhone" class="sr-only peer">
                      <div class="w-9 h-5 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-500/20
                        rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                        after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border
                        after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                    <span class="text-sm text-gray-300">Telefoon</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" formControlName="showEmail" class="sr-only peer">
                      <div class="w-9 h-5 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-500/20
                        rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                        after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border
                        after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                    <span class="text-sm text-gray-300">E-mail</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" formControlName="showSocials" class="sr-only peer">
                      <div class="w-9 h-5 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-500/20
                        rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['']
                        after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border
                        after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                    <span class="text-sm text-gray-300">Socials</span>
                  </div>
                </div>
              </div>
            </div>
          </form>

        </div>
      </div>

    </div>
  </div>
</div>

<!-- Floating Save All Button -->
<div *ngIf="currentDomain && !loading && !errorMessage"
     class="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 flex justify-end">
    <button
      (click)="saveAllSettings()"
      [disabled]="savingAll"
      class="pointer-events-auto inline-flex items-center gap-2.5 px-7 py-3.5 text-sm font-semibold rounded-2xl
             text-white transition-all duration-300 transform hover:scale-105
             focus:outline-none focus:ring-4 focus:ring-indigo-500/30 focus:ring-offset-2 focus:ring-offset-gray-900
             disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
      [ngClass]="savingAll ? 'bg-indigo-700' : 'bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600'"
      style="box-shadow: 0 8px 32px rgba(99, 102, 241, 0.35), 0 2px 8px rgba(0,0,0,0.3);">
      <svg *ngIf="!savingAll" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
      </svg>
      <svg *ngIf="savingAll" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      {{ savingAll ? 'Opslaan...' : 'Alles Opslaan' }}
    </button>
  </div>
</div>

<!-- Add Language Modal -->
<div *ngIf="showAddLanguageModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
  <!-- Background backdrop -->
  <div class="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity" (click)="closeAddLanguageModal()"></div>

  <!-- Modal container -->
  <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
    <div class="relative transform overflow-hidden rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 px-6 pb-6 pt-6 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-8 border border-gray-700/50" (click)="$event.stopPropagation()">
      <!-- Close button -->
      <button
        (click)="closeAddLanguageModal()"
        class="absolute right-4 top-4 text-gray-400 hover:text-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg p-1"
        aria-label="Close modal">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- Modal content -->
      <div class="mt-2">
        <h3 class="text-lg font-semibold leading-6 text-gray-200 mb-2" id="modal-title">
          Add Language
        </h3>
        <p class="text-sm text-gray-400 mb-6">
          Select a country to add its primary language to your website
        </p>

        <!-- Search input -->
        <div class="mb-4">
          <label for="countrySearch" class="block text-sm font-medium text-gray-300 mb-2">
            Search Countries
          </label>
          <input
            type="text"
            id="countrySearch"
            [(ngModel)]="countrySearchTerm"
            (input)="filterCountries()"
            placeholder="Type to search by country name, language, or code..."
            class="block w-full px-4 py-3 border border-gray-600 rounded-lg bg-gray-700/50 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            autocomplete="off"
          />
        </div>

        <!-- Country list -->
        <div class="max-h-96 overflow-y-auto border border-gray-700 rounded-lg bg-gray-900/50">
          <div *ngIf="filteredCountries.length === 0" class="p-8 text-center text-gray-400">
            <p>No countries found matching your search.</p>
          </div>
          <div *ngIf="filteredCountries.length > 0" class="divide-y divide-gray-700">
            <button
              *ngFor="let country of filteredCountries"
              type="button"
              (click)="selectCountry(country)"
              class="w-full px-4 py-3 text-left hover:bg-gray-800/50 transition-colors focus:outline-none focus:bg-gray-800/50"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-3">
                    <span class="text-lg font-semibold text-gray-300">{{ country.name }}</span>
                    <span class="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400 border border-gray-600">
                      {{ country.code }}
                    </span>
                  </div>
                  <div class="mt-1 flex items-center gap-2">
                    <span class="text-sm text-gray-400">{{ country.languageName }}</span>
                    <span class="text-xs text-gray-500">({{ country.languageCode.toUpperCase() }})</span>
                  </div>
                </div>
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-6 flex justify-end">
          <button
            type="button"
            (click)="closeAddLanguageModal()"
            class="inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 focus:ring-offset-gray-900 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<div *ngIf="notificationService.notification$ | async as notification"
     class="fixed bottom-24 right-4 z-50 flex items-center p-4 rounded-lg shadow-lg"
     [ngClass]="notification.type === 'success' ? 'bg-green-800 text-green-100' : 'bg-red-800 text-red-100'">
  <svg *ngIf="notification.type === 'success'" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
  </svg>
  <svg *ngIf="notification.type === 'error'" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
  </svg>
  {{ notification.message }}
</div> 